import type {
  MailAccountDetail,
  MailFolder,
  MailMessageDetail,
  MailMessageSummary,
  PublicMailAccount,
  Provider,
} from "@mailstorm/shared";

export type PublicUser = {
  id: string;
  email: string;
  displayName: string | null;
  role: "user" | "admin";
};

export type Preferences = {
  theme: string;
  density: string;
  readingPane: string;
  defaultAccountId: string | null;
};

export type CalendarAccount = {
  id: string;
  name: string;
  url: string;
  color: string;
  provider: string;
};

export type CalendarEvent = {
  id: string;
  calendarId: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  location?: string;
  description?: string;
};

export type Contact = {
  id: string;
  displayName: string;
  email: string;
  phone?: string;
  notes?: string;
  source: "local" | "carddav" | "recent";
};

export type AccountHealth = {
  accountId: string;
  status: "ok" | "warn" | "error";
  message: string;
  checkedAt: string;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };
  const method = (init?.method || "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") {
    const csrf = document.cookie
      .split("; ")
      .find((r) => r.startsWith("mailstorm_csrf="))
      ?.split("=")[1];
    if (csrf) headers["X-CSRF-Token"] = decodeURIComponent(csrf);
  }

  let res: Response;
  try {
    res = await fetch(path, { credentials: "include", ...init, headers });
  } catch (err) {
    throw new Error(err instanceof Error ? err.message : "Failed to fetch");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      typeof data?.error === "string"
        ? data.error
        : data?.error
          ? JSON.stringify(data.error)
          : res.statusText;
    throw new Error(msg);
  }
  return data as T;
}

export const api = {
  me: () => request<{ user: PublicUser }>("/api/auth/me"),
  login: async (email: string, password: string) => {
    const result = await request<{ user: PublicUser }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    await request("/api/auth/csrf").catch(() => undefined);
    return result;
  },
  logout: () => request("/api/auth/logout", { method: "POST" }),
  csrf: () => request<{ csrf: string }>("/api/auth/csrf"),

  accounts: () => request<{ accounts: PublicMailAccount[] }>("/api/accounts"),
  account: (id: string) =>
    request<{ account: MailAccountDetail }>(`/api/accounts/${encodeURIComponent(id)}`),
  presets: () => request<{ presets: Record<string, unknown> }>("/api/accounts/presets"),
  addImap: (body: unknown) =>
    request<{ account: PublicMailAccount }>("/api/accounts/imap", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateAccount: (id: string, body: unknown) =>
    request<{ account: MailAccountDetail }>(`/api/accounts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteAccount: (id: string) => request(`/api/accounts/${id}`, { method: "DELETE" }),
  reorderAccounts: (accountIds: string[]) =>
    request<{ accounts: PublicMailAccount[] }>("/api/accounts/reorder", {
      method: "PUT",
      body: JSON.stringify({ accountIds }),
    }),
  oauthStart: (provider: "gmail" | "microsoft") =>
    request<{ url: string }>(`/api/accounts/oauth/${provider}/start`),
  health: () => request<{ items: AccountHealth[] }>("/api/accounts/health"),

  folders: (accountId: string) =>
    request<{ folders: MailFolder[] }>(`/api/mail/folders?accountId=${encodeURIComponent(accountId)}`),
  messages: (q: {
    accountId?: string;
    folder?: string;
    page?: number;
    pageSize?: number;
    search?: string;
    smart?: string;
    bustCache?: boolean;
  }) => {
    const p = new URLSearchParams();
    if (q.accountId) p.set("accountId", q.accountId);
    if (q.folder) p.set("folder", q.folder);
    if (q.page) p.set("page", String(q.page));
    if (q.pageSize) p.set("pageSize", String(q.pageSize));
    if (q.search) p.set("search", q.search);
    if (q.smart) p.set("smart", q.smart);
    if (q.bustCache) p.set("_", String(Date.now()));
    return request<{ total: number; messages: MailMessageSummary[]; unified?: boolean }>(
      `/api/mail/messages?${p}`,
    );
  },
  message: (accountId: string, folder: string, uid: number) =>
    request<{ message: MailMessageDetail }>(
      `/api/mail/messages/${uid}?accountId=${encodeURIComponent(accountId)}&folder=${encodeURIComponent(folder)}`,
    ),
  flags: (body: unknown) => request("/api/mail/flags", { method: "POST", body: JSON.stringify(body) }),
  markFolderSeen: (accountId: string, folder: string) =>
    request<{ ok: boolean; marked: number }>("/api/mail/mark-folder-seen", {
      method: "POST",
      body: JSON.stringify({ accountId, folder }),
    }),
  move: (body: unknown) => request("/api/mail/move", { method: "POST", body: JSON.stringify(body) }),
  send: (body: unknown) => request<{ messageId: string }>("/api/mail/send", { method: "POST", body: JSON.stringify(body) }),
  bulk: (body: unknown) => request("/api/mail/bulk", { method: "POST", body: JSON.stringify(body) }),
  undo: (body: unknown) => request("/api/mail/undo", { method: "POST", body: JSON.stringify(body) }),
  signatures: (accountId?: string) =>
    request<{
      signatures: Array<{ id: string; accountId: string | null; name: string; html: string; isDefault: boolean }>;
    }>(`/api/mail/signatures${accountId ? `?accountId=${encodeURIComponent(accountId)}` : ""}`),
  saveSignature: (body: unknown) =>
    request("/api/mail/signatures", { method: "POST", body: JSON.stringify(body) }),
  cacheSearch: (q: string) =>
    request<{ messages: Array<Record<string, unknown>> }>(
      `/api/mail/cache/search?q=${encodeURIComponent(q)}`,
    ),

  preferences: () => request<{ preferences: Preferences }>("/api/preferences"),
  savePreferences: (body: unknown) =>
    request("/api/preferences", { method: "PUT", body: JSON.stringify(body) }),

  adminUsers: () =>
    request<{
      users: Array<{ id: string; email: string; displayName: string | null; role: string; createdAt: string }>;
    }>("/api/admin/users"),
  adminCreateUser: (body: unknown) =>
    request("/api/admin/users", { method: "POST", body: JSON.stringify(body) }),
  adminUpdateUser: (id: string, body: unknown) =>
    request<{
      user: { id: string; email: string; displayName: string | null; role: string; createdAt: string };
    }>(`/api/admin/users/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  adminDeleteUser: (id: string) =>
    request<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(id)}`, { method: "DELETE" }),
  adminAudit: () => request<{ events: unknown[] }>("/api/admin/audit"),

  calendars: () => request<{ calendars: CalendarAccount[] }>("/api/calendar/accounts"),
  addCalendar: (body: unknown) =>
    request<{ calendar: CalendarAccount }>("/api/calendar/accounts", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  syncCalendar: (id: string) =>
    request<{ ok: boolean; imported: number }>(`/api/calendar/accounts/${id}/sync`, { method: "POST" }),
  deleteCalendar: (id: string) => request(`/api/calendar/accounts/${id}`, { method: "DELETE" }),
  events: (from: string, to: string) =>
    request<{ events: CalendarEvent[] }>(
      `/api/calendar/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
  saveEvent: (body: unknown) =>
    request<{ event: CalendarEvent }>("/api/calendar/events", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteEvent: (id: string, calendarId: string) =>
    request(`/api/calendar/events/${id}?calendarId=${encodeURIComponent(calendarId)}`, {
      method: "DELETE",
    }),

  contacts: () => request<{ contacts: Contact[] }>("/api/contacts"),
  suggestContacts: (q: string) =>
    request<{ contacts: Array<{ displayName: string; email: string }> }>(
      `/api/contacts/suggest?q=${encodeURIComponent(q)}`,
    ),
  saveContact: (body: unknown) =>
    request<{ contact: Contact }>("/api/contacts", { method: "POST", body: JSON.stringify(body) }),
  deleteContact: (id: string) => request(`/api/contacts/${id}`, { method: "DELETE" }),
  addCardDav: (body: unknown) =>
    request("/api/contacts/carddav", { method: "POST", body: JSON.stringify(body) }),

  jmapSession: (accountId: string) =>
    request<{ available: boolean; capabilities: string[]; note: string }>(
      `/api/platform/jmap/session?accountId=${encodeURIComponent(accountId)}`,
    ),
  idleWatch: (accountIds: string[], enabled = true) =>
    request("/api/platform/idle/watch", {
      method: "POST",
      body: JSON.stringify({ accountIds, enabled }),
    }),
  idleStatus: () =>
    request<{
      watching: string[];
      snapshots: Array<{ accountId: string; unseen: number; checkedAt: string }>;
    }>("/api/platform/idle/status"),
  idleRefresh: () =>
    request<{
      watching: string[];
      snapshots: Array<{ accountId: string; unseen: number; checkedAt: string }>;
    }>("/api/platform/idle/refresh", { method: "POST", body: "{}" }),
  acceptInvite: (body: unknown) =>
    request("/api/platform/itip/accept", { method: "POST", body: JSON.stringify(body) }),
  featureFlags: () => request<{ flags: Record<string, boolean> }>("/api/platform/feature-flags"),
};

export type { PublicMailAccount, MailAccountDetail, MailFolder, MailMessageSummary, MailMessageDetail, Provider };
